/**
 * Unified Amazon Chime SDK Meetings adapter for 1:1 calls.
 *
 * A single adapter powers BOTH voice and video. Meetings are always created
 * video-capable (MaxResolution HD, attendees Video:SendReceive), so a voice
 * call simply joins audio-only (camera never started) and a later voice→video
 * switch just mounts the video tiles + starts the local video tile — there is
 * NO media reconnect / re-join, which is what makes voice↔video switching
 * reliable instead of dropping the call.
 *
 * It also drives Google-Meet-style screen sharing via Chime content share
 * (getDisplayMedia under the hood). The incoming content arrives as a separate
 * "#content" attendee tile, which is bound to the dedicated content element.
 */
import {
  BackgroundBlurVideoFrameProcessor,
  BackgroundReplacementVideoFrameProcessor,
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingSession,
  DefaultVideoTransformDevice,
  LogLevel,
  MeetingSessionConfiguration,
  type Device,
  type VideoFrameProcessor,
  type VideoInputDevice,
  type VideoTileState,
} from 'amazon-chime-sdk-js'
import type { ChimeJoinTokenResponse } from '../chime-service'

/** Local camera background filter applied before frames are sent to the meeting. */
export type AdapterBackgroundFilter =
  | { kind: 'none' }
  | { kind: 'blur' }
  | { kind: 'image'; imageUrl: string }
import { pickAudioInputDeviceId } from '../../utils/call-audio-input'
import { classifyMeetingStop } from '../../utils/call-connection-guards'
import { isNativeApp, getNativePlatform } from '../../utils/platform'
import { callAudioRouteArbiter } from '../../utils/call-audio-route-arbiter'
import {
  applyDesiredNativeSpeakerphone,
  resetNativeAudioRoute,
  setNativeSpeakerphone,
} from './native-audio-route'
import { recordCallFlow } from '../../debug/call-flow-log'

export type ChimeRosterAttendee = {
  attendeeId: string
  /** Chime ExternalUserId — equals the app userId (cognito sub, ≤64 chars). */
  externalUserId: string
}

/** Cognito subs (our externalUserId) are v4 UUIDs. */
const COGNITO_SUB_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** When the 1:1 remote audio level is sampled after the peer joins. */
const REMOTE_LEVEL_SAMPLE_MS = 5_000

/**
 * The Chime media-capture / recording pipeline joins the meeting roster as a
 * synthetic `MediaPipeline-*` attendee (its attendeeId AND externalUserId carry
 * that prefix). It is NOT a human participant and must never surface as a tile
 * or roster entry — otherwise it renders as an unresolved "Participant" tile in
 * multiparty calls. Recording stays ON; we just never show this attendee.
 *
 * Defensive: also treat any externalUserId that is neither a Cognito-sub UUID
 * nor a "<sub>#content" content-share id as non-human, so future service-side
 * pipeline/bot attendees can't leak into the roster either.
 */
const isNonHumanAttendee = (attendeeId: string, externalUserId?: string | null): boolean => {
  const ext = externalUserId ?? ''
  if (/^MediaPipeline/i.test(attendeeId) || /^MediaPipeline/i.test(ext)) return true
  // No external id resolved yet → can't classify; treat as human (presence
  // tracking still resolves the real id later) unless the attendeeId itself
  // looks synthetic, which is handled above.
  if (!ext) return false
  const base = ext.endsWith('#content') ? ext.slice(0, -'#content'.length) : ext
  // A genuine human external id is the Cognito sub (UUID). Anything else that
  // is not UUID-shaped and not our own id is a non-human/pipeline attendee.
  return !COGNITO_SUB_SHAPE.test(base)
}

export type ChimeRemoteTile = {
  tileId: number
  attendeeId: string
  externalUserId: string
  isContent: boolean
}

export type ChimeCallAdapterEvents = {
  onConnected: () => void
  /**
   * The FIRST remote human attendee is present in the Chime roster — i.e.
   * two-way media is actually established (both parties are in the meeting).
   * This is the CANONICAL "call connected" moment used to start the in-app
   * timer + stamp the recorded duration, distinct from {@link onConnected}
   * (which can fire on LOCAL media start, before the peer has joined). Fires
   * once per session, in both 1:1 and multiparty mode.
   */
  onRemotePresent?: () => void
  onDisconnected: () => void
  onRemoteLeft: () => void
  onError: (message: string) => void
  /**
   * Chime is (re)establishing the media connection. `reconnecting` is true only
   * for a transient mid-call reconnect (not the initial connect), which the UI
   * surfaces as a "Reconnecting…" banner.
   */
  onConnecting?: (reconnecting: boolean) => void
  /**
   * The Chime media session terminated BEFORE it ever successfully started
   * (i.e. the initial connect failed). `code` is the Chime
   * `MeetingSessionStatusCode`. This is distinct from {@link onDisconnected}
   * (a mid-call drop of an established session): on iOS the WKWebView signaling
   * WebSocket can be suspended/torn down at the CallKit-answer handoff, and the
   * SDK maps that abnormal close during the initial join to a TERMINAL
   * `SignalingBadRequest` (code 11) that it never retries — so the controller
   * must decide whether to rejoin.
   */
  onConnectFailed?: (code: number) => void
  /**
   * Chime dropped THIS attendee because the same user joined the meeting from
   * ANOTHER device (`AudioJoinedFromAnotherDevice`, multi-device Switch). This
   * must be handled as a silent local handoff to "elsewhere" — mirroring the
   * app-level DEVICE_CLAIM signal — and must NEVER take the disconnect path
   * (whose grace timer broadcasts END/INTERRUPTED and can end the call for
   * every participant when the DEVICE_CLAIM is delayed or lost).
   */
  onKickedByOtherDevice?: () => void
  /**
   * The meeting itself ended server-side (`MeetingEnded`) while media was up.
   * The call is over for everyone — end locally without the disconnect grace
   * and without notifying the peer (there is nobody left to notify).
   */
  onMeetingEnded?: () => void
  /** Our own screen share started/stopped (covers the browser "Stop sharing"). */
  onLocalContentShareChanged?: (active: boolean) => void
  /** The peer started/stopped presenting their screen. */
  onRemoteContentShareChanged?: (active: boolean) => void
  /**
   * A remote (non-content) camera tile became active and unpaused — proof the
   * peer is sending video. Used to clear a stale peer-driving placeholder if
   * the peer's `driving:false` announcement was lost in transit.
   */
  onRemoteVideoActive?: () => void

  // --- Multi-party (group) callbacks (only fire when joined with multiparty) ---
  /** A remote attendee joined / left the meeting (Chime presence). */
  onRosterPresenceChanged?: (attendee: ChimeRosterAttendee, present: boolean) => void
  /** A remote attendee's mic state / speaking changed (volume indicator). */
  onAttendeeAudioStateChanged?: (
    attendee: ChimeRosterAttendee,
    state: { muted: boolean; speaking: boolean },
  ) => void
  /** A remote (non-content) video tile arrived — bind it via bindRemoteTile. */
  onRemoteTileAdded?: (tile: ChimeRemoteTile) => void
  /** A remote video tile was removed. */
  onRemoteTileRemoved?: (tileId: number) => void
  /** Chime hit the 25-sender ceiling — local video can't be sent right now. */
  onVideoSendUnavailable?: () => void
}

export type ChimeCallElements = {
  audio: HTMLAudioElement
  /** Optional until the video overlay mounts (audio-only / pre-switch calls). */
  localVideo?: HTMLVideoElement | null
  remoteVideo?: HTMLVideoElement | null
  /** Optional element that renders the incoming screen-share (content) tile. */
  contentVideo?: HTMLVideoElement | null
}

function avVideoLabelTrigger(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({ audio: true, video: true })
}

function audioOnlyLabelTrigger(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({ audio: true, video: false })
}

function stopStreamTracks(stream: MediaStream | null | undefined): void {
  if (!stream) return
  for (const track of stream.getTracks()) {
    try {
      track.stop()
    } catch {
      /* already stopped */
    }
  }
}

export class ChimeCallAdapter {
  private meetingSession: DefaultMeetingSession | null = null
  private deviceController: DefaultDeviceController | null = null
  private elements: ChimeCallElements | null = null
  private localAudioStream: MediaStream | null = null
  private muted = false
  private cameraOff = false
  /** True once video input has been started (camera live this session). */
  private videoEnabled = false
  private connected = false
  /**
   * True once Chime fired `audioVideoDidStart` — i.e. media REALLY connected at
   * least once this session. Unlike {@link connected} (which `join()` also sets
   * optimistically right after `audioVideo.start()`), this only flips on the
   * SDK's real connected signal, so a terminal `audioVideoDidStop` can tell an
   * initial-connect failure (never started) apart from a mid-call drop.
   */
  private mediaStarted = false
  private remotePeerPresent = false
  /**
   * True once the first remote human attendee has been seen this session, so
   * {@link ChimeCallAdapterEvents.onRemotePresent} fires exactly once (the
   * canonical two-way-media connect moment).
   */
  private remotePresentFired = false
  /** True once a remote 1:1 peer has produced audible volume this session. */
  private remoteAudioHeard = false
  /** 1:1 remote volume seen since the peer joined (the post-connect level sample). */
  private remoteLevelMax = 0
  private remoteLevelSamples = 0
  private remoteLevelTimer: ReturnType<typeof setTimeout> | null = null
  /**
   * 1:1 remote video stays paused (not received, not bound) until
   * {@link enableVideo} / {@link resumeRemoteVideo} — a video call answered on
   * a locked Android phone.
   */
  private remoteVideoDeferred = false
  /**
   * True while dispose() runs AND permanently afterwards — an adapter instance
   * is one-shot (the controller creates a fresh adapter per join), so once
   * disposed no late SDK callback / timer may ever touch this instance again.
   */
  private disposing = false
  /** iOS post-start route reasserts — cleared on dispose so none fire post-hangup. */
  private speakerReassertTimers: Array<ReturnType<typeof setTimeout>> = []
  /** Tile id of the peer's content (screen-share) tile, when they're presenting. */
  private remoteContentTileId: number | null = null
  /** Multi-party mode — disables the 1:1 single-remote-peer coupling. */
  private multiparty = false
  /** Our own attendee id (to skip self in roster/tile callbacks). */
  private localAttendeeId: string | null = null
  /**
   * Our own external user id (Cognito sub). A reloaded client rejoins with a
   * NEW attendeeId but the SAME externalUserId; skipping by externalUserId (not
   * just attendeeId) stops a stale self-attendee from rendering as a phantom
   * remote peer — the "2 of you" / "it merged a call" grid after a WKWebView
   * reload (2026-07-07).
   */
  private localExternalUserId: string | null = null
  /**
   * 1:1 mode: attendeeIds of the remote peer currently present. A peer that
   * reloads briefly has TWO attendeeIds (old ghost + new); only fire
   * onRemoteLeft when the LAST one leaves, so a reload doesn't drop the call.
   */
  private remoteAttendeeIds = new Set<string>()
  /** True between a transient reconnect start and its completion. */
  private reconnecting = false
  /** attendeeId → externalUserId, for volume-indicator unsubscribe + roster. */
  private attendeeExternalIds = new Map<string, string>()
  /** Per-attendee volume-indicator unsubscribe callbacks. */
  private volumeUnsubs = new Map<string, () => void>()
  /** Logger reused for background-filter transform devices. */
  private logger: ConsoleLogger | null = null
  /** Device id of the active camera (so a filter swap re-uses the same camera). */
  private currentVideoDeviceId: string | null = null
  /** Active local background filter (default: raw camera). */
  private backgroundFilter: AdapterBackgroundFilter = { kind: 'none' }
  /** Active transform device + processor, kept so they can be torn down on swap. */
  private transformDevice: DefaultVideoTransformDevice | null = null
  private backgroundProcessor: VideoFrameProcessor | null = null

  constructor(private readonly events: ChimeCallAdapterEvents) {}

  /**
   * Signal the canonical two-way-media connect the first time a remote human
   * attendee is present. Idempotent per session (see {@link remotePresentFired}).
   */
  private fireRemotePresent(): void {
    if (this.remotePresentFired || this.disposing) return
    this.remotePresentFired = true
    if (!this.multiparty) this.scheduleRemoteLevelSample()
    this.events.onRemotePresent?.()
  }

  /** "Connected but I can't hear them": how loud the peer was a few seconds in. */
  private scheduleRemoteLevelSample(): void {
    if (this.remoteLevelTimer) return
    this.remoteLevelTimer = setTimeout(() => {
      this.remoteLevelTimer = null
      if (this.disposing) return
      const audio = this.elements?.audio
      recordCallFlow('chime:remote:audioLevel', {
        afterMs: REMOTE_LEVEL_SAMPLE_MS,
        maxVolume: Math.round(this.remoteLevelMax * 100) / 100,
        samples: this.remoteLevelSamples,
        heard: this.remoteAudioHeard,
        audioElPaused: audio ? audio.paused : null,
        audioElMuted: audio ? audio.muted : null,
      })
    }, REMOTE_LEVEL_SAMPLE_MS)
  }

  private bindTile(audioVideo: DefaultMeetingSession['audioVideo'], tileState: VideoTileState): void {
    // Do not gate on tileState.active — Chime sets active only after boundVideoElement is set.
    if (!this.elements || !tileState.tileId || !tileState.boundAttendeeId) return

    if (tileState.isContent) {
      // Our own screen-share preview would create a feedback loop when sharing
      // the app tab — skip binding it. The peer's content tile is the main view.
      if (tileState.localTile) return
      const contentEl = this.elements.contentVideo
      if (!contentEl) return
      audioVideo.bindVideoElement(tileState.tileId, contentEl)
      void contentEl.play().catch(() => {
        /* autoplay may require a user gesture on some browsers */
      })
      return
    }

    if (this.remoteVideoDeferred && !tileState.localTile) {
      if (!tileState.paused) {
        try {
          audioVideo.pauseVideoTile(tileState.tileId)
        } catch {
          /* tile already gone */
        }
      }
      return
    }

    // Multi-party: remote tiles are bound per-participant by the overlay via
    // bindRemoteTile(). Only the local self-view binds to a fixed element here.
    if (this.multiparty && !tileState.localTile) {
      const tileExternalId =
        tileState.boundExternalUserId ??
        this.attendeeExternalIds.get(tileState.boundAttendeeId) ??
        tileState.boundAttendeeId
      // Never surface the recording pipeline's video tile (it has none, but a
      // synthetic attendee could still produce a phantom bound tile).
      if (isNonHumanAttendee(tileState.boundAttendeeId, tileState.boundExternalUserId)) return
      this.events.onRemoteTileAdded?.({
        tileId: tileState.tileId,
        attendeeId: tileState.boundAttendeeId,
        externalUserId: tileExternalId,
        isContent: false,
      })
      return
    }

    const target = tileState.localTile ? this.elements.localVideo : this.elements.remoteVideo
    // Video elements may not be mounted yet on an audio-only / pre-switch call;
    // the tile is rebound by updateElements once the video overlay mounts.
    if (!target) return
    audioVideo.bindVideoElement(tileState.tileId, target)
    void target.play().catch(() => {
      /* autoplay may require user gesture on some browsers */
    })
  }

  /** Multi-party: bind a specific remote tile id to a participant's element. */
  bindRemoteTile(tileId: number, element: HTMLVideoElement): void {
    const audioVideo = this.meetingSession?.audioVideo
    if (!audioVideo) return
    try {
      audioVideo.bindVideoElement(tileId, element)
      void element.play().catch(() => {
        /* autoplay may require a user gesture */
      })
    } catch {
      /* tile may have been removed */
    }
  }

  unbindRemoteTile(tileId: number): void {
    const audioVideo = this.meetingSession?.audioVideo
    if (!audioVideo) return
    try {
      audioVideo.unbindVideoElement(tileId)
    } catch {
      /* already unbound */
    }
  }

  /** Re-bind when overlay audio element mounts or changes (web race). */
  updateAudioElement(audioElement: HTMLAudioElement): void {
    if (!this.elements) return
    this.elements = { ...this.elements, audio: audioElement }
    const audioVideo = this.meetingSession?.audioVideo
    if (!audioVideo) return
    try {
      audioVideo.bindAudioElement(audioElement)
      audioElement.autoplay = true
      void audioElement.play().catch(() => {
        /* autoplay may require user gesture */
      })
    } catch {
      /* join will bind on first attach */
    }
  }

  /** Re-bind tiles when overlay video / content elements mount or change. */
  updateElements(
    elements: Pick<ChimeCallElements, 'localVideo' | 'remoteVideo' | 'contentVideo'>,
  ): void {
    if (!this.elements) return
    this.elements = { ...this.elements, ...elements }
    const audioVideo = this.meetingSession?.audioVideo
    if (!audioVideo) return
    for (const tile of audioVideo.getAllVideoTiles()) {
      this.bindTile(audioVideo, tile.state())
    }
  }

  async join(
    credentials: ChimeJoinTokenResponse,
    userId: string,
    elements: ChimeCallElements,
    options?: {
      enableVideo?: boolean
      multiparty?: boolean
      speakerOn?: boolean
      /**
       * Video call resilience: when true, a camera that cannot start (e.g. a
       * CallKit-answered iOS call joining while the WebView is still
       * backgrounded, where getUserMedia video is blocked) does NOT fail the
       * whole join — the call connects audio-only and the camera is retried
       * when the app foregrounds (see controller) or the user toggles it.
       */
      videoOptional?: boolean
      /**
       * 1:1 audio-only join that keeps the peer's video paused until
       * enableVideo() / resumeRemoteVideo() (Android locked-phone answer).
       */
      deferRemoteVideo?: boolean
    },
  ): Promise<void> {
    if (this.disposing) throw new Error('Call adapter already disposed')
    // Voice calls pass enableVideo:false and start audio-only so they can switch
    // to video in place; video calls start the camera immediately.
    const enableVideo = options?.enableVideo !== false
    // Arbiter owns the target: join-time options must not freeze a stale
    // speakerOn that later fights an in-call user toggle (2.5s reassert burst).
    const speakerOn = callAudioRouteArbiter.isActive()
      ? callAudioRouteArbiter.desiredSpeakerOn()
      : (options?.speakerOn ?? enableVideo)
    this.multiparty = options?.multiparty === true
    this.remoteVideoDeferred = options?.deferRemoteVideo === true && !this.multiparty
    this.localAttendeeId = credentials.attendeeId
    this.localExternalUserId = userId || null
    this.elements = elements

    recordCallFlow('chime:join:start', {
      enableVideo,
      multiparty: this.multiparty,
      speakerOn,
      videoOptional: options?.videoOptional === true,
      ...(this.remoteVideoDeferred ? { remoteVideoDeferred: true } : {}),
    })

    // Android: pin the in-call route at the very start of the join. The
    // WebView's own WebRTC/audio stack re-routes to the LOUD SPEAKER around
    // getUserMedia/capture start, defeating the one-shot earpiece pin the
    // ringing phase applied — on the 2026-08-19 Zach→Andre call the route read
    // speaker from join start until the (single) corrective below landed ~4s
    // later, blasting the connecting disclosure through the loudspeaker. Pin
    // here, again after mic capture, and burst-reassert after start() (mirrors
    // the iOS reasserts). Best-effort and cheap — never blocks the join.
    if (isNativeApp() && getNativePlatform() === 'android') {
      void applyDesiredNativeSpeakerphone()
    }

    const logger = new ConsoleLogger('ChimeCall', LogLevel.WARN)
    this.logger = logger
    const deviceController = new DefaultDeviceController(logger)
    this.deviceController = deviceController
    deviceController.setDeviceLabelTrigger(
      enableVideo ? avVideoLabelTrigger : audioOnlyLabelTrigger,
    )

    const meetingPayload = credentials.meeting
    const attendeePayload = credentials.attendee
    if (!meetingPayload?.Meeting?.MediaPlacement?.AudioHostUrl) {
      throw new Error('Call server returned incomplete meeting placement (audioHostUrl missing)')
    }

    const configuration = new MeetingSessionConfiguration(meetingPayload, attendeePayload)
    const session = new DefaultMeetingSession(configuration, logger, deviceController)
    this.meetingSession = session
    const { audioVideo } = session

    audioVideo.addObserver({
      audioVideoDidStartConnecting: (reconnecting: boolean) => {
        if (this.disposing) return
        recordCallFlow('chime:audioVideoDidStartConnecting', { reconnecting })
        // Only surface a transient mid-call reconnect; the initial connect is
        // covered by the "Connecting…" phase already.
        if (reconnecting) {
          this.reconnecting = true
          this.events.onConnecting?.(true)
        }
      },
      audioVideoDidStart: () => {
        if (this.disposing) return
        recordCallFlow('chime:audioVideoDidStart', { wasReconnecting: this.reconnecting })
        // Media truly connected — from here a stop is a mid-call DROP, not an
        // initial-connect failure (see audioVideoDidStop / mediaStarted).
        this.mediaStarted = true
        // A reconnect just completed — `onConnected` won't re-fire (guarded by
        // `connected`), so clear the reconnecting flag explicitly here.
        if (this.reconnecting) {
          this.reconnecting = false
          this.events.onConnecting?.(false)
        }
        if (this.connected) return
        this.connected = true
        this.events.onConnected()
      },
      audioVideoDidStop: (sessionStatus) => {
        const code = sessionStatus?.statusCode?.()
        const numericCode = typeof code === 'number' ? code : null
        const action = classifyMeetingStop({
          code: numericCode,
          mediaStarted: this.mediaStarted,
          connected: this.connected,
          disposing: this.disposing,
        })
        recordCallFlow('chime:audioVideoDidStop', {
          code: numericCode,
          mediaStarted: this.mediaStarted,
          connected: this.connected,
          action,
          // A stop during dispose() is expected teardown, not a live failure —
          // stamped so post-hangup stops can't be misread as connect failures.
          disposing: this.disposing,
        })
        if (this.disposing) return
        this.connected = false
        switch (action) {
          case 'kicked-other-device':
            // Same user joined from another device — silent local handoff, no
            // disconnect grace / END broadcast (see onKickedByOtherDevice doc).
            // Fall back to the legacy paths only on an old controller wiring.
            if (this.events.onKickedByOtherDevice) {
              this.events.onKickedByOtherDevice()
              return
            }
            break
          case 'meeting-ended':
            if (this.events.onMeetingEnded) {
              this.events.onMeetingEnded()
              return
            }
            break
          case 'ignore':
            return
          default:
            break
        }
        // Terminated BEFORE media ever started = an initial-connect failure. The
        // SDK does NOT retry terminal signaling codes (e.g. 11 SignalingBadRequest,
        // which is what an abnormal WKWebView signaling-socket close during the
        // initial join maps to). Hand it to the controller so it can decide to
        // rejoin (iOS CallKit-handoff socket suspension is transient) instead of
        // the call silently dying. Do NOT also fire onDisconnected here.
        if (action === 'connect-failed' && this.events.onConnectFailed) {
          this.events.onConnectFailed(numericCode ?? -1)
          return
        }
        this.events.onDisconnected()
      },
      videoTileDidUpdate: (tileState) => {
        if (
          tileState.isContent &&
          !tileState.localTile &&
          tileState.tileId &&
          this.remoteContentTileId !== tileState.tileId
        ) {
          this.remoteContentTileId = tileState.tileId
          this.events.onRemoteContentShareChanged?.(true)
        }
        // A live (unpaused) remote camera tile proves the peer is sending
        // video again — the controller uses this to clear a stale "driving"
        // placeholder when the peer's resume announcement was lost.
        if (
          !tileState.localTile &&
          !tileState.isContent &&
          tileState.tileId &&
          tileState.active &&
          !tileState.paused &&
          !this.remoteVideoDeferred
        ) {
          this.events.onRemoteVideoActive?.()
        }
        this.bindTile(audioVideo, tileState)
      },
      videoTileWasRemoved: (tileId) => {
        if (this.remoteContentTileId === tileId) {
          this.remoteContentTileId = null
          this.events.onRemoteContentShareChanged?.(false)
        }
        if (this.multiparty) this.events.onRemoteTileRemoved?.(tileId)
      },
      // 25-sender ceiling — local camera can't be sent right now (group video).
      videoSendDidBecomeUnavailable: () => {
        if (this.disposing) return
        this.events.onVideoSendUnavailable?.()
      },
    })

    // Our own screen-share lifecycle (also fires when the user clicks the
    // browser's native "Stop sharing" control).
    audioVideo.addContentShareObserver({
      contentShareDidStart: () => this.events.onLocalContentShareChanged?.(true),
      contentShareDidStop: () => this.events.onLocalContentShareChanged?.(false),
    })

    audioVideo.realtimeSubscribeToAttendeeIdPresence(
      (attendeeId, present, externalUserId) => {
        if (this.disposing) return
        // The content-share attendee is "<attendeeId>#content"; never treat its
        // presence as the human peer joining / leaving.
        if (attendeeId === this.localAttendeeId || attendeeId.endsWith('#content')) return

        // A reloaded client rejoins with a NEW attendeeId but the SAME
        // externalUserId. Our OWN stale attendee (from before a WKWebView
        // reload) must never be treated as a remote peer, or it renders as a
        // duplicate/phantom participant ("2 of you"). Skip anything that shares
        // our own external id.
        const presenceExternalId =
          externalUserId ?? this.attendeeExternalIds.get(attendeeId) ?? null
        if (
          this.localExternalUserId &&
          presenceExternalId &&
          presenceExternalId === this.localExternalUserId
        ) {
          return
        }

        // The media-capture/recording pipeline joins as a synthetic
        // `MediaPipeline-*` attendee. Drop it entirely so it never becomes a
        // roster entry, volume subscription, or "Participant" tile. Recording
        // is unaffected — we just don't render the pipeline as a person.
        if (isNonHumanAttendee(attendeeId, externalUserId)) return

        const resolvedExternalId =
          externalUserId ?? this.attendeeExternalIds.get(attendeeId) ?? attendeeId

        // Always track present human attendees (in BOTH 1:1 and multiparty mode)
        // so the roster can be reconciled against Chime's authoritative attendee
        // list, and so a 1:1→group upgrade can replay everyone already present.
        if (present) {
          this.attendeeExternalIds.set(attendeeId, resolvedExternalId)
        }

        if (this.multiparty) {
          // Group call: liveness + roster come from per-attendee presence. No
          // single "remote left → end" coupling; the controller decides when the
          // call ends (roster→1 or host end-for-all).
          if (present) {
            this.events.onRosterPresenceChanged?.(
              { attendeeId, externalUserId: resolvedExternalId },
              true,
            )
            this.subscribeAttendeeVolume(attendeeId, resolvedExternalId)
            // First remote human present = two-way media established.
            this.fireRemotePresent()
          } else {
            this.events.onRosterPresenceChanged?.(
              { attendeeId, externalUserId: resolvedExternalId },
              false,
            )
            this.unsubscribeAttendeeVolume(attendeeId)
            this.attendeeExternalIds.delete(attendeeId)
          }
          return
        }

        // 1:1 behavior: the remote peer joining/leaving drives connect +
        // remote-left. A peer that reloads mid-call briefly has TWO attendeeIds
        // (stale ghost + fresh); track the set and only fire onRemoteLeft when
        // the LAST one leaves, so a reload doesn't tear down a live call.
        if (present) {
          this.remoteAttendeeIds.add(attendeeId)
          this.remotePeerPresent = true
          if (!this.connected) {
            this.connected = true
            this.events.onConnected()
          }
          // First remote peer present = two-way media established (the canonical
          // connect moment; onConnected above may have fired earlier on LOCAL
          // media start, before the peer joined).
          this.fireRemotePresent()
          this.subscribeAttendeeVolume(attendeeId, resolvedExternalId)
          return
        }
        this.attendeeExternalIds.delete(attendeeId)
        this.unsubscribeAttendeeVolume(attendeeId)
        this.remoteAttendeeIds.delete(attendeeId)
        if (this.remotePeerPresent && this.remoteAttendeeIds.size === 0) {
          this.remotePeerPresent = false
          // Re-arm the remote-present signal: if the peer comes back (device
          // Switch re-joins with a new attendeeId, reload, ICE flap) the
          // controller must learn about it so it can cancel the remote-left
          // grace instead of ending a call that just healed.
          this.remotePresentFired = false
          this.events.onRemoteLeft()
        }
      },
    )

    audioVideo.bindAudioElement(elements.audio)

    // A hangup while this join is still in flight runs dispose() concurrently
    // (it nulls meetingSession). Every await below must re-check ownership so
    // an aborted join can never start an orphaned meeting connection or leave
    // the mic captured after the call UI is gone.
    const assertJoinStillCurrent = () => {
      if (this.disposing || this.meetingSession !== session) {
        throw new Error('Call ended before the meeting join completed')
      }
    }

    const audioInputs = await audioVideo.listAudioInputDevices()
    assertJoinStillCurrent()
    if (audioInputs.length === 0) {
      throw new Error('No microphone found. Connect a microphone to place calls.')
    }
    const inputDeviceId = pickAudioInputDeviceId(audioInputs) ?? audioInputs[0].deviceId

    try {
      const stream = await audioVideo.startAudioInput(inputDeviceId)
      if (this.disposing || this.meetingSession !== session) {
        // dispose() already ran its stopAudioInput before this capture started —
        // release the late mic stream ourselves or it stays captured forever.
        stopStreamTracks(stream)
        try {
          await audioVideo.stopAudioInput()
        } catch {
          /* already stopped */
        }
        throw new Error('Call ended before the meeting join completed')
      }
      if (stream) this.localAudioStream = stream
      const track = stream?.getAudioTracks?.()[0]
      recordCallFlow('chime:mic:started', {
        inputCount: audioInputs.length,
        trackReadyState: track?.readyState ?? null,
        trackEnabled: track?.enabled ?? null,
        trackMuted: track?.muted ?? null,
        trackLabel: track?.label ?? null,
      })
      // Android: capture start is exactly when the WebView's WebRTC stack
      // clobbers the route (see the join-start pin above) — re-pin right away
      // rather than waiting for the pre-start set below.
      if (isNativeApp() && getNativePlatform() === 'android') {
        void applyDesiredNativeSpeakerphone()
      }
    } catch (err) {
      if (err instanceof Error && /before the meeting join completed/.test(err.message)) {
        throw err
      }
      recordCallFlow('chime:mic:failed', { message: err instanceof Error ? err.message : String(err) })
      throw new Error('Microphone access denied. Allow microphone permission for calls.')
    }

    if (enableVideo) {
      try {
        const videoInputs = await audioVideo.listVideoInputDevices()
        if (videoInputs.length === 0) {
          throw new Error('No camera found. Connect a camera to place video calls.')
        }
        try {
          this.currentVideoDeviceId = videoInputs[0].deviceId
          await audioVideo.startVideoInput(await this.buildVideoInputDevice(videoInputs[0].deviceId))
        } catch {
          throw new Error('Camera access denied. Allow camera permission for video calls.')
        }
        this.videoEnabled = true
      } catch (err) {
        if (!options?.videoOptional) throw err
        // Degrade to an audio-only join — the call must connect even when the
        // camera can't start yet. videoEnabled stays false so enableVideo()
        // performs the full camera start on retry.
        this.videoEnabled = false
      }
      if (this.disposing || this.meetingSession !== session) {
        // dispose() ran while the camera was starting — release it.
        try {
          await audioVideo.stopVideoInput()
        } catch {
          /* already stopped */
        }
        throw new Error('Call ended before the meeting join completed')
      }
    }

    // Android: pre-activate the in-call route BEFORE Chime starts its WebRTC
    // audio unit. Android's AudioManager needs MODE_IN_COMMUNICATION set before
    // start() so the audio unit binds to an active in-call session — otherwise
    // the call connects with no audible audio in either direction.
    //
    // iOS: DO NOT touch the audio session here. WebKit owns the AVAudioSession
    // (.playAndRecord) for getUserMedia; a pre-start app-side setCategory/
    // setActive interrupts WebKit's capture session and silences the mic uplink
    // (dead mic, bug #1). The iOS route is applied AFTER start() below, once
    // WebKit's .playAndRecord session exists (override-only, safe within it).
    if (isNativeApp() && getNativePlatform() === 'android') {
      await applyDesiredNativeSpeakerphone()
    }

    assertJoinStillCurrent()
    recordCallFlow('chime:audioVideo:start:before', { speakerOn, videoEnabled: this.videoEnabled })
    await audioVideo.start()
    recordCallFlow('chime:audioVideo:start:after', {})
    if (this.disposing || this.meetingSession !== session) {
      // Hangup landed between start() being issued and completing — stop the
      // orphaned media connection dispose() could not see yet.
      try {
        audioVideo.stop()
      } catch {
        /* already stopped */
      }
      throw new Error('Call ended before the meeting join completed')
    }
    if (enableVideo && this.videoEnabled) audioVideo.startLocalVideoTile()

    // iOS: now that WebKit's capture session is live (.playAndRecord exists),
    // set earpiece/speaker via the native output override. This is safe within
    // WebKit's session and does not interrupt capture. WebKit/Chime often
    // re-clobber the route for ~1s after start (outgoing voice stuck on loud
    // speaker while JS still has isSpeakerOn=false) — retry a few times; the
    // native AudioRoutePlugin also arms its own burst reassert.
    //
    // Android gets the same post-start burst: Chromium's WebRTC audio manager
    // can re-route to the loud speaker when playout starts (audioVideoDidStart
    // lands ~1s after start()), silently undoing the pre-start earpiece pin.
    if (isNativeApp()) {
      if (getNativePlatform() === 'ios') {
        await applyDesiredNativeSpeakerphone()
      }
      for (const delayMs of [150, 450, 1000, 2500]) {
        const timer = setTimeout(() => {
          if (this.disposing) return
          void applyDesiredNativeSpeakerphone()
        }, delayMs)
        this.speakerReassertTimers.push(timer)
      }
    }

    // Re-bind any tiles that arrived before elements were ready (web race).
    for (const tileState of audioVideo.getAllVideoTiles()) {
      this.bindTile(audioVideo, tileState.state())
    }

    elements.audio.autoplay = true
    const playRemote = (attempt: 1 | 2) => {
      void elements.audio.play().then(
        () => {
          recordCallFlow(attempt === 1 ? 'chime:audioEl:playing' : 'chime:audioEl:playRetryOk', {})
        },
        (err: unknown) => {
          recordCallFlow(attempt === 1 ? 'chime:audioEl:playFailed' : 'chime:audioEl:playRetryFailed', {
            message: err instanceof Error ? err.message : String(err),
          })
          if (attempt === 1 && !this.disposing) {
            setTimeout(() => {
              if (this.disposing) return
              playRemote(2)
            }, 400)
          }
        },
      )
    }
    playRemote(1)

    if (!this.disposing && !this.connected) {
      this.connected = true
      this.events.onConnected()
    }
  }

  async setSpeakerphone(on: boolean): Promise<boolean> {
    if (isNativeApp()) {
      const nativeOk = await setNativeSpeakerphone(on)
      if (nativeOk) return true
    }

    const audioVideo = this.meetingSession?.audioVideo
    if (!audioVideo) return false
    try {
      const outputs = await audioVideo.listAudioOutputDevices()
      if (outputs.length === 0) return false
      if (on) {
        const speaker = outputs.find((d) => /speaker/i.test(d.label))
        if (!speaker) return false
        await audioVideo.chooseAudioOutput(speaker.deviceId)
      } else {
        const earpiece = outputs.find((d) => /earpiece|receiver|default/i.test(d.label))
        await audioVideo.chooseAudioOutput(earpiece ? earpiece.deviceId : null)
      }
      return true
    } catch {
      return false
    }
  }

  /** Multi-party: track a remote attendee's mic state + speaking via volume. */
  private subscribeAttendeeVolume(attendeeId: string, externalUserId: string): void {
    const audioVideo = this.meetingSession?.audioVideo
    if (!audioVideo || this.volumeUnsubs.has(attendeeId)) return
    const callback = (
      _attendeeId: string,
      volume: number | null,
      muted: boolean | null,
    ): void => {
      if (this.disposing) return
      if (!this.multiparty) {
        if (typeof volume === 'number') {
          this.remoteLevelSamples += 1
          if (volume > this.remoteLevelMax) this.remoteLevelMax = volume
        }
        if (muted === true) {
          recordCallFlow('chime:remote:muted', { attendeeId, muted: true })
        } else if (typeof volume === 'number' && volume > 0.05 && !this.remoteAudioHeard) {
          this.remoteAudioHeard = true
          recordCallFlow('chime:remote:audioHeard', { attendeeId, volume })
        }
      }
      this.events.onAttendeeAudioStateChanged?.(
        { attendeeId, externalUserId },
        {
          muted: muted === true,
          speaking: muted !== true && typeof volume === 'number' && volume > 0.05,
        },
      )
    }
    try {
      audioVideo.realtimeSubscribeToVolumeIndicator(attendeeId, callback)
      this.volumeUnsubs.set(attendeeId, () => {
        try {
          audioVideo.realtimeUnsubscribeFromVolumeIndicator(attendeeId, callback)
        } catch {
          /* already gone */
        }
      })
    } catch {
      /* attendee left before subscribe */
    }
  }

  private unsubscribeAttendeeVolume(attendeeId: string): void {
    const unsub = this.volumeUnsubs.get(attendeeId)
    if (unsub) {
      unsub()
      this.volumeUnsubs.delete(attendeeId)
    }
  }

  /**
   * Switch from 1:1 to multi-party presence handling without re-joining.
   * Call when a live 1:1 meeting gains a third participant (add people / transfer).
   */
  enableMultipartyMode(): void {
    if (this.multiparty) return
    this.multiparty = true
    this.remotePeerPresent = false

    const audioVideo = this.meetingSession?.audioVideo
    if (!audioVideo) return

    // Replay every attendee already present in the meeting (tracked via the
    // attendee-presence subscription, audio + video) so the roster converges
    // immediately after a 1:1→group upgrade — not just those sending video.
    for (const [attendeeId, externalUserId] of this.attendeeExternalIds) {
      if (
        !attendeeId ||
        !externalUserId ||
        attendeeId === this.localAttendeeId ||
        attendeeId.endsWith('#content') ||
        isNonHumanAttendee(attendeeId, externalUserId)
      ) {
        continue
      }
      this.events.onRosterPresenceChanged?.({ attendeeId, externalUserId }, true)
      this.subscribeAttendeeVolume(attendeeId, externalUserId)
    }

    // Re-bind any remote video tiles that pre-date the switch so they render in
    // the per-participant group grid (1:1 bound them to the fixed remote element).
    for (const tile of audioVideo.getAllVideoTiles()) {
      const state = tile.state()
      if (state.isContent || state.localTile || !state.tileId || !state.boundAttendeeId) {
        continue
      }
      if (state.boundAttendeeId === this.localAttendeeId) continue
      const externalUserId =
        state.boundExternalUserId ?? this.attendeeExternalIds.get(state.boundAttendeeId)
      if (!externalUserId) continue
      this.events.onRemoteTileAdded?.({
        tileId: state.tileId,
        attendeeId: state.boundAttendeeId,
        externalUserId,
        isContent: false,
      })
    }
  }

  /**
   * External user ids (Cognito sub) of every human attendee currently present
   * in the meeting, excluding our own attendee and content-share attendees. Used
   * by the controller to reconcile the displayed roster against the authoritative
   * Chime attendee list (drop ghost/stale "active" participants).
   */
  getPresentExternalUserIds(): string[] {
    const ids = new Set<string>()
    for (const [attendeeId, externalUserId] of this.attendeeExternalIds) {
      if (!externalUserId) continue
      if (attendeeId === this.localAttendeeId || attendeeId.endsWith('#content')) continue
      if (this.localExternalUserId && externalUserId === this.localExternalUserId) continue
      if (externalUserId.endsWith('#content')) continue
      if (isNonHumanAttendee(attendeeId, externalUserId)) continue
      ids.add(externalUserId)
    }
    return Array.from(ids)
  }

  /**
   * Restart the local audio input on the existing meeting session, preserving
   * mute state. Used when the native audio session's lifecycle invalidated the
   * audio unit Chime bound at join time — e.g. iOS CallKit activating the
   * AVAudioSession AFTER audioVideo.start() ran (cold-start answers), which
   * otherwise leaves a connected call with a dead mic. Best-effort.
   */
  async restartAudioInput(): Promise<void> {
    const audioVideo = this.meetingSession?.audioVideo
    if (!audioVideo || this.disposing) return
    recordCallFlow('chime:restartAudioInput:start', {})
    try {
      const inputs = await audioVideo.listAudioInputDevices()
      if (inputs.length === 0) {
        recordCallFlow('chime:restartAudioInput:noInputs', {})
        return
      }
      const inputDeviceId = pickAudioInputDeviceId(inputs) ?? inputs[0].deviceId
      const stream = await audioVideo.startAudioInput(inputDeviceId)
      if (stream) this.localAudioStream = stream
      this.setMuted(this.muted)
      const track = stream?.getAudioTracks?.()[0]
      recordCallFlow('chime:restartAudioInput:done', {
        trackReadyState: track?.readyState ?? null,
        muted: this.muted,
      })
    } catch (err) {
      recordCallFlow('chime:restartAudioInput:failed', {
        message: err instanceof Error ? err.message : String(err),
      })
      /* device busy / permission transient — the user can toggle mute to retry */
    }
  }

  setMuted(muted: boolean): void {
    this.muted = muted
    const audioVideo = this.meetingSession?.audioVideo
    if (!audioVideo) return
    if (muted) audioVideo.realtimeMuteLocalAudio()
    else audioVideo.realtimeUnmuteLocalAudio()
  }

  /** Low-level local video tile control (kept thin for the controller). */
  startLocalVideoTile(): void {
    this.meetingSession?.audioVideo.startLocalVideoTile()
  }

  stopLocalVideoTile(): void {
    try {
      this.meetingSession?.audioVideo.stopLocalVideoTile()
    } catch {
      /* ignore */
    }
  }

  async setCameraEnabled(enabled: boolean): Promise<void> {
    this.cameraOff = !enabled
    const audioVideo = this.meetingSession?.audioVideo
    if (!audioVideo) return
    if (enabled) {
      // Camera input may never have started (audio-only degrade of a video
      // join, or a voice call pre-upgrade) — enabling the tile alone would
      // show a black self-view. Run the full camera start in that case.
      if (!this.videoEnabled) {
        await this.enableVideo()
        return
      }
      audioVideo.startLocalVideoTile()
    } else {
      audioVideo.stopLocalVideoTile()
    }
  }

  /** True when the local camera input is live for this meeting session. */
  /** Whether the Chime media session has connected (audioVideoDidStart). */
  isConnected(): boolean {
    return this.connected
  }

  /**
   * Whether at least one remote HUMAN attendee is currently present in the
   * meeting (Chime roster truth). Lets the controller cross-check app-level
   * liveness signals (peer heartbeats over AppSync) against the media plane
   * before declaring the peer gone.
   */
  hasRemotePresence(): boolean {
    if (this.multiparty) {
      for (const [attendeeId, externalUserId] of this.attendeeExternalIds) {
        if (attendeeId === this.localAttendeeId || attendeeId.endsWith('#content')) continue
        if (this.localExternalUserId && externalUserId === this.localExternalUserId) continue
        if (isNonHumanAttendee(attendeeId, externalUserId)) continue
        return true
      }
      return false
    }
    return this.remotePeerPresent
  }

  isVideoStarted(): boolean {
    return this.videoEnabled
  }

  /**
   * Mid-call voice→video switch WITHOUT re-joining: start the camera + local
   * video tile on the existing (video-capable) attendee, then bind the now-
   * mounted video elements. Idempotent.
   */
  async enableVideo(
    elements?: Pick<ChimeCallElements, 'localVideo' | 'remoteVideo' | 'contentVideo'>,
  ): Promise<void> {
    const audioVideo = this.meetingSession?.audioVideo
    if (!audioVideo) throw new Error('Not connected to a meeting')
    if (elements) this.elements = { ...this.elements!, ...elements }

    this.resumeRemoteVideo()
    if (!this.videoEnabled) {
      this.deviceController?.setDeviceLabelTrigger(avVideoLabelTrigger)
      const videoInputs = await audioVideo.listVideoInputDevices()
      if (videoInputs.length === 0) {
        throw new Error('No camera found. Connect a camera to use video.')
      }
      try {
        this.currentVideoDeviceId = videoInputs[0].deviceId
        await audioVideo.startVideoInput(await this.buildVideoInputDevice(videoInputs[0].deviceId))
      } catch {
        throw new Error('Camera access denied. Allow camera permission for video.')
      }
      this.videoEnabled = true
    }

    this.cameraOff = false
    audioVideo.startLocalVideoTile()
    for (const tile of audioVideo.getAllVideoTiles()) {
      this.bindTile(audioVideo, tile.state())
    }
  }

  /**
   * Stop receiving + showing the peer's video mid-call (1:1 only) — the
   * car-mode driving pause. Mirrors the deferred-join state, so tiles that
   * arrive while paused are parked by {@link bindTile} and
   * {@link resumeRemoteVideo} undoes everything. Idempotent.
   */
  pauseRemoteVideo(): void {
    if (this.remoteVideoDeferred || this.multiparty) return
    this.remoteVideoDeferred = true
    const audioVideo = this.meetingSession?.audioVideo
    if (!audioVideo) return
    for (const tile of audioVideo.getAllVideoTiles()) {
      const state = tile.state()
      if (state.localTile || !state.tileId || state.isContent) continue
      if (!state.paused) {
        try {
          audioVideo.pauseVideoTile(state.tileId)
        } catch {
          /* tile already gone */
        }
      }
    }
  }

  /** Start receiving + showing the peer's video that a deferred join paused. Idempotent. */
  resumeRemoteVideo(): void {
    if (!this.remoteVideoDeferred) return
    this.remoteVideoDeferred = false
    const audioVideo = this.meetingSession?.audioVideo
    if (!audioVideo) return
    for (const tile of audioVideo.getAllVideoTiles()) {
      const state = tile.state()
      if (state.localTile || !state.tileId) continue
      if (state.paused) {
        try {
          audioVideo.unpauseVideoTile(state.tileId)
        } catch {
          /* tile already gone */
        }
      }
      this.bindTile(audioVideo, state)
    }
  }

  /**
   * Mid-call video→voice switch WITHOUT leaving the meeting: stop sending local
   * video and release the camera. The audio session stays connected.
   */
  async disableVideo(): Promise<void> {
    const audioVideo = this.meetingSession?.audioVideo
    if (!audioVideo) return
    try {
      audioVideo.stopLocalVideoTile()
    } catch {
      /* ignore */
    }
    if (this.videoEnabled) {
      try {
        await audioVideo.stopVideoInput()
      } catch {
        /* ignore */
      }
      this.videoEnabled = false
    }
    this.cameraOff = true
  }

  /**
   * Build the camera input device for {@link this.backgroundFilter}: either the
   * raw device id, or a {@link DefaultVideoTransformDevice} wrapping a Chime
   * background blur / replacement processor. Falls back to the raw camera if the
   * processor isn't supported or its assets fail to load — the call never drops.
   */
  private async buildVideoInputDevice(deviceId: string): Promise<VideoInputDevice> {
    await this.disposeTransformDevice()
    if (this.backgroundFilter.kind === 'none') return deviceId
    const logger = this.logger ?? new ConsoleLogger('ChimeCall', LogLevel.WARN)
    try {
      let processor: VideoFrameProcessor | undefined
      if (this.backgroundFilter.kind === 'blur') {
        if (!(await BackgroundBlurVideoFrameProcessor.isSupported())) return deviceId
        processor = await BackgroundBlurVideoFrameProcessor.create()
      } else {
        if (!(await BackgroundReplacementVideoFrameProcessor.isSupported())) return deviceId
        const imageBlob = await fetch(this.backgroundFilter.imageUrl).then((r) => r.blob())
        processor = await BackgroundReplacementVideoFrameProcessor.create(undefined, { imageBlob })
      }
      if (!processor) return deviceId
      this.backgroundProcessor = processor
      const transform = new DefaultVideoTransformDevice(logger, deviceId as Device, [processor])
      this.transformDevice = transform
      return transform
    } catch {
      // Filter assets/WASM failed to load — degrade gracefully to the raw camera.
      await this.disposeTransformDevice()
      return deviceId
    }
  }

  private async disposeTransformDevice(): Promise<void> {
    const transform = this.transformDevice
    const processor = this.backgroundProcessor
    this.transformDevice = null
    this.backgroundProcessor = null
    if (transform) {
      try {
        await transform.stop()
      } catch {
        /* already stopped */
      }
    }
    if (processor) {
      try {
        await processor.destroy()
      } catch {
        /* already destroyed */
      }
    }
  }

  /**
   * Swap the local camera background filter (none / blur / replacement image).
   * No-op stash when the camera isn't live yet — the filter is applied the next
   * time video starts (initial join or {@link enableVideo}).
   */
  async setBackgroundFilter(filter: AdapterBackgroundFilter): Promise<void> {
    this.backgroundFilter = filter
    const audioVideo = this.meetingSession?.audioVideo
    if (!audioVideo || !this.videoEnabled) return
    let deviceId = this.currentVideoDeviceId
    if (!deviceId) {
      const inputs = await audioVideo.listVideoInputDevices()
      deviceId = inputs[0]?.deviceId ?? null
    }
    if (!deviceId) return
    await audioVideo.startVideoInput(await this.buildVideoInputDevice(deviceId))
  }

  getBackgroundFilter(): AdapterBackgroundFilter {
    return this.backgroundFilter
  }

  /**
   * Start sharing the screen/window/tab (web only — uses getDisplayMedia via
   * Chime content share). Throws if the user cancels the picker or the browser
   * denies capture; the caller surfaces that.
   */
  async startContentShare(): Promise<void> {
    const audioVideo = this.meetingSession?.audioVideo
    if (!audioVideo) throw new Error('Not connected to a meeting')
    await audioVideo.startContentShareFromScreenCapture()
  }

  async stopContentShare(): Promise<void> {
    const audioVideo = this.meetingSession?.audioVideo
    if (!audioVideo) return
    try {
      await audioVideo.stopContentShare()
    } catch {
      /* already stopped */
    }
  }

  isMuted(): boolean {
    return this.muted
  }

  isCameraOff(): boolean {
    return this.cameraOff
  }

  disposeSync(): void {
    for (const timer of this.speakerReassertTimers) clearTimeout(timer)
    this.speakerReassertTimers = []
    if (this.remoteLevelTimer) {
      clearTimeout(this.remoteLevelTimer)
      this.remoteLevelTimer = null
    }
    resetNativeAudioRoute()
    stopStreamTracks(this.localAudioStream)
    this.localAudioStream = null

    if (this.elements?.audio) {
      try {
        this.elements.audio.pause()
        this.elements.audio.srcObject = null
        this.elements.audio.removeAttribute('src')
      } catch {
        /* ignore */
      }
    }
  }

  async dispose(): Promise<void> {
    if (this.disposing) return
    this.disposing = true

    this.disposeSync()
    await this.disposeTransformDevice()
    this.backgroundFilter = { kind: 'none' }
    this.currentVideoDeviceId = null

    const session = this.meetingSession
    const deviceController = this.deviceController

    this.meetingSession = null
    this.deviceController = null
    this.elements = null
    // NOTE: `connected` / `mediaStarted` are reset AFTER the session teardown
    // below — audioVideo.stop() fires audioVideoDidStop synchronously into the
    // observer above, and zeroing the flags first made every post-hangup stop
    // log as a spurious "connect-failed" (mediaStarted:false on a session that
    // demonstrably started — 2026-08-19 debugging red herring).
    this.remotePeerPresent = false
    this.remotePresentFired = false
    this.remoteAudioHeard = false
    this.remoteLevelMax = 0
    this.remoteLevelSamples = 0
    this.remoteVideoDeferred = false
    this.muted = false
    this.cameraOff = false
    this.videoEnabled = false
    this.remoteContentTileId = null
    for (const unsub of this.volumeUnsubs.values()) unsub()
    this.volumeUnsubs.clear()
    this.attendeeExternalIds.clear()
    this.multiparty = false
    this.localAttendeeId = null
    this.localExternalUserId = null
    this.remoteAttendeeIds.clear()
    this.logger = null

    if (session) {
      const { audioVideo } = session
      try {
        audioVideo.realtimeMuteLocalAudio()
      } catch {
        /* ignore */
      }
      try {
        await audioVideo.stopContentShare()
      } catch {
        /* ignore */
      }
      try {
        audioVideo.stopLocalVideoTile()
      } catch {
        /* ignore */
      }
      try {
        await audioVideo.stopAudioInput()
      } catch {
        /* ignore */
      }
      try {
        await audioVideo.stopVideoInput()
      } catch {
        /* ignore */
      }
      try {
        audioVideo.unbindAudioElement()
      } catch {
        /* ignore */
      }
      try {
        audioVideo.stop()
      } catch {
        /* already stopped */
      }
    }

    this.connected = false
    this.mediaStarted = false

    if (deviceController) {
      try {
        await deviceController.destroy()
      } catch {
        /* ignore */
      }
    }

    // Deliberately LEAVE `disposing` true: adapter instances are one-shot (the
    // controller builds a new adapter per join). Resetting it re-armed late SDK
    // callbacks and post-dispose timers against a dead instance — e.g. an
    // in-flight join() resuming after hangup and starting an orphaned meeting
    // connection, or route-reassert timers flipping the speakerphone after the
    // call ended.
  }
}
